import React from 'react'
import _ from 'lodash'

import { formatBRL, formatToCurrency, formatUSD } from 'utils/functions'

import { makeStyles } from '@material-ui/core'
import { Remove } from '@material-ui/icons'
import AlertMessage from 'ui/components/AlertMessage'
import Container from './styled_container'
import styles from './mui_styles'

const IrSummary = ({ usdData, receiptData, irDeclaration, settings }) => {
  const classes = makeStyles(() => styles)()
  const showUsdColumn = settings?.calculate_values_in_usd
  const tableColumnCount = showUsdColumn ? 3 : 2


  const dolarValues = {
    quotation_usd: {
      value: usdData?.sale_quotation_usd,
      label: 'Cotação de Venda',
      currency: 'USD',
    },
    quotation_datetime: { value: usdData?.quotation_datetime, label: 'Data da Cotação' },
  }

  const summaryItems = [
    {
      label: 'Total de Rendimentos Tributáveis',
      bold: true,
      value: irDeclaration?.overall_summary?.total_taxable_income ?? receiptData?.taxable_income,
      valueUsd: irDeclaration?.overall_summary?.total_taxable_income_usd ?? receiptData?.taxable_income_usd,
      present: irDeclaration?.overall_summary?.total_taxable_income != null || receiptData?.taxable_income != null,
      group: 'Rendimentos Tributáveis',
    },
    {
      label: `Total de Rendimentos Tributáveis (${settings?.receipt_taxable_income_percent_for_calc}%)`,
      value: receiptData?.taxable_income_deducted,
      valueUsd: receiptData?.taxable_income_deducted_usd,
      enabled:
        receiptData?.taxable_income_deducted != null &&
        settings?.receipt_taxable_income_percent_for_calc != null &&
        settings?.receipt_taxable_income_percent_for_calc !== 100,
      group: 'Rendimentos Tributáveis',
    },
      {
      label: 'Bens e Direitos e Direitos Declarados',
      bold: true,
      value: irDeclaration?.assets_declaration?.current_year_total_value,
      valueUsd: irDeclaration?.assets_declaration?.current_year_total_value_usd,
      present: irDeclaration?.assets_declaration?.current_year_total_value != null,
      group: 'Bens e Direitos',
    },
    {
      label: 'Bens e Direitos Líquidos Declarados',
      value: irDeclaration?.assets_declaration?.current_year_total_liquid_value || irDeclaration?.total_liquid_value,
      valueUsd: irDeclaration?.assets_declaration?.current_year_total_liquid_value_usd,
      enabled: [irDeclaration?.assets_declaration?.current_year_total_liquid_value, irDeclaration?.total_liquid_value].some((value) => value != null),
      group: 'Bens e Direitos',
    },
    {
      label: ` Bens e Direitos Líquidos Declarados (${settings?.total_liquid_value_percent_for_calc}%)`,
      value: irDeclaration?.assets_declaration?.current_year_total_liquid_value_percent,
      valueUsd: irDeclaration?.assets_declaration?.current_year_total_liquid_value_percent_usd,
      enabled:
        irDeclaration?.assets_declaration?.current_year_total_liquid_value_percent != null &&
        settings?.total_liquid_value_percent_for_calc != null &&
        settings?.total_liquid_value_percent_for_calc !== 100,
      group: 'Bens e Direitos',
    },
    {
      label: 'Total em Dívidas e Ônus Reais',
      value: irDeclaration?.overall_summary?.total_debts_value_current_year,
      valueUsd: irDeclaration?.overall_summary?.total_debts_value_current_year_usd,
      present: irDeclaration?.overall_summary?.total_debts_value_current_year != null,
      group: 'Dívidas e Ônus Reais',
      bold: true,
    },
    {
      label: 'Rendimentos Isentos e Não Tributáveis',
      bold: true,
      value: irDeclaration?.exempt_income?.total_value,
      valueUsd: irDeclaration?.exempt_income?.total_value_usd,
      present: irDeclaration?.exempt_income?.total_value != null,
      group: 'Rendimentos Tributáveis',
    },
    {
      label: 'Rendimentos Sujeitos à Tributação Exclusiva/Definitiva',
      bold: true,
      value: irDeclaration?.exclusive_taxation_income?.total_value,
      valueUsd: irDeclaration?.exclusive_taxation_income?.total_value_usd,
      present: irDeclaration?.exclusive_taxation_income?.total_value != null,
      group: 'Rendimentos Tributáveis',
    },
        {
      label: 'Resultado Tributável da Atividade Rural',
      bold: true,
      value: irDeclaration?.overall_summary?.taxable_rural_activity_result,
      valueUsd: irDeclaration?.overall_summary?.taxable_rural_activity_result_usd,
      present: irDeclaration?.overall_summary?.taxable_rural_activity_result != null,
      group: 'Atividade Rural',
    },
    {
      label: 'Evolução Patrimonial',
      bold: true,
      value: irDeclaration?.overall_summary?.equity_evolution_value,
      valueUsd: irDeclaration?.overall_summary?.equity_evolution_value_usd,
      present: irDeclaration?.overall_summary?.equity_evolution_value != null,
      group: 'Resultado',
    },
    {
      label: 'Resultado (Somatório de Rendimentos e Bens Líquidos)',
      bold: true,
      value: _.sum(
        [
          (irDeclaration?.overall_summary?.total_taxable_income ?? receiptData?.taxable_income),
          irDeclaration?.exempt_income?.total_value,
          irDeclaration?.exclusive_taxation_income?.total_value,
          irDeclaration?.total_liquid_value,
        ].map((value) => (_.isNil(value) ? 0 : value)),
      ),
      valueUsd: _.sum(
        [
          (irDeclaration?.overall_summary?.total_taxable_income_usd ?? receiptData?.taxable_income_usd),
          irDeclaration?.exempt_income?.total_value_usd,
          irDeclaration?.exclusive_taxation_income?.total_value_usd,
          irDeclaration?.total_liquid_value_usd,
        ].map((value) => (_.isNil(value) ? 0 : value)),
      ),
      present: _.every(
        [
          irDeclaration?.overall_summary?.total_taxable_income ?? receiptData?.taxable_income,
          irDeclaration?.exempt_income?.total_value,
          irDeclaration?.exclusive_taxation_income?.total_value,
          irDeclaration?.total_liquid_value,
        ],
        (value) => !_.isNil(value),
      ),
      group: 'Resultado',
    },
  ]

  const summaryGroups = _.chain(summaryItems).groupBy('group').values().value()

  const visibleGroups = summaryGroups
    .map((group) => group.filter((item) => item?.enabled !== false))
    .filter((group) => !_.isEmpty(group))

  const renderValueIfPresent = (value, is_present) => (
    is_present === false ? <Remove style={{ fontSize: '1.3rem' }} /> : value
  )

  const rows = []
  let contentRowIndex = 0

  visibleGroups.forEach((group, groupIndex) => {
    group.forEach((item) => {
      rows.push(
        <tr key={item.label} className={contentRowIndex % 2 === 1 ? classes.zebraRow : null}>
          <td style={{ fontSize: '1.4rem' }}>
            <strong style={{ fontWeight: 600 }}>
              {`${item.bold ? '' : '\u00A0\u00A0  • '}${item.label}`}
            </strong>
          </td>
          <td style={{ fontSize: '1.4rem' }}>
            <span>{renderValueIfPresent(formatBRL(item.value), item.present)}</span>
          </td>
          {showUsdColumn && (
            <td style={{ fontSize: '1.4rem' }}>
              <span>{renderValueIfPresent(formatUSD(item.valueUsd), item.present)}</span>
            </td>
          )}
        </tr>,
      )

      contentRowIndex += 1
    })

    if (groupIndex < visibleGroups.length - 1) {
      rows.push(
        <tr
          key={`spacer-${group.map((item) => item.label).join('|')}`}
          className={classes.groupSpacer}
        >
          <td colSpan={tableColumnCount} />
        </tr>,
      )
    }
  })

  return (
    <React.Fragment>
      <Container>
        <h2>
          <ul>
            <li>Resumo</li>
          </ul>
        </h2>
      </Container>

      {settings?.acceptable_taxable_income_usd && (
      <AlertMessage
        iconType="error"
        severity="error"
        title={`Os rendimentos tributáveis totais declarados são menores que ${formatUSD(settings?.acceptable_taxable_income_usd)}`}
      />
      )}
      <Container>
        <div className={classes.tableContainer}>
          <table className={classes.table} style={{ fontSize: '1.5rem' }}>
            <thead>
              <tr>
                <th>Item</th>
                <th>Valor em Reais</th>
                {showUsdColumn && <th>Valor em USD</th>}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>

        <br />

        {settings?.calculate_values_in_usd && (
          <div className={classes.tableContainer} style={{ fontSize: '1.5rem' }}>
            <h4>Dólar</h4>
            <table className={classes.dolarTable} style={{ marginTop: '0.5rem' }}>
              <tbody>
                {Object.entries(dolarValues).map(([key, { value, label, currency }]) => (
                  <tr key={key}>
                    <td>
                      <strong style={{ fontWeight: 600 }}>{label}</strong>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span>
                        {' '}
                        {currency ? formatToCurrency(value, { withSymbol: true, currency }) : value}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Container>
    </React.Fragment>
  )
}

export default IrSummary
